import morph from "../../helpers/morph";
import bindBloomreachEvents from "../../helpers/bloomreach";
import "./product-card-carousel";
import { loadQuickAdd } from "./quick-add";
import { openWaitlist } from "../waitlist/waitlist";
import {
  openFindInStore,
  prefetchFindInStore,
} from "../find-in-store/find-in-store";

const QUICK_ADD_MODAL_ID = "quick-add-modal";
const ERROR_DURATION = 3000;

// How far a pointer may travel before a click counts as a swipe, not a tap.
const DRAG_THRESHOLD = 10;

class ProductCard extends HTMLElement {
  connectedCallback() {
    this.addEventListener("change", (e) => {
      this._changeContent(e.target.value);
    });

    this.addEventListener("click", this._onQuickAddClick);
    this.addEventListener("click", this._onFindInStoreClick);
    this.addEventListener("pointerover", this._prefetchQuickAdd);
    this.addEventListener("focusin", this._prefetchQuickAdd);

    this._navigateOnTap();

    bindBloomreachEvents();
  }

  disconnectedCallback() {
    clearTimeout(this._errorTimeout);
  }

  // Warms the cache on hover or focus, so the modal usually opens on content
  // that's already arrived.
  _prefetchQuickAdd = (event) => {
    const trigger = event.target.closest("[data-quick-add-url]");

    if (trigger && !trigger.dataset.quickAddWaitlist) {
      loadQuickAdd(trigger.dataset.quickAddUrl).catch(() => {});
    }

    const findInStore = event.target.closest("[data-find-in-store-url]");
    if (findInStore) prefetchFindInStore(findInStore.dataset.findInStoreUrl);
  };

  // Read on click, like the quick add triggers: a swatch change morphs in
  // another colourway's URL.
  _onFindInStoreClick = (event) => {
    const trigger = event.target.closest("[data-find-in-store-url]");
    if (!trigger) return;

    openFindInStore(trigger, trigger.dataset.findInStoreUrl);
  };

  // The triggers are read on every click: a swatch change morphs in another
  // colourway's product, and with it that product's variant or URL.
  _onQuickAddClick = (event) => {
    const trigger = event.target.closest("[data-quick-add]");
    if (!trigger) return;

    const {
      quickAddVariant,
      quickAddPreorder,
      quickAddNote,
      quickAddUrl,
      quickAddWaitlist,
    } = trigger.dataset;

    if (quickAddWaitlist) {
      // Single variant, sold out: straight to the waitlist
      openWaitlist(trigger, quickAddUrl, quickAddWaitlist);
    } else if (quickAddVariant) {
      // Single variant: straight to the cart
      this._addToCart(trigger, quickAddVariant, quickAddPreorder, quickAddNote);
    } else if (quickAddUrl) {
      // Several variants: choose in the modal
      this._openQuickAdd(trigger, quickAddUrl);
    }
  };

  _openQuickAdd(trigger, url) {
    const html = loadQuickAdd(url);

    html.catch(() => window.location.assign(this.productUrl));

    trigger.dispatchEvent(
      new CustomEvent("markup-modal:open", {
        bubbles: true,
        detail: { target: QUICK_ADD_MODAL_ID, html },
      }),
    );
  }

  /**
   * @param {HTMLElement} trigger
   * @param {string} id Variant id
   * @param {string} [preorder] The variant's `Preorder` line item property
   *   value, if it's a preorder
   * @param {string} [note] The product's promo tag `Note` line item property
   *   value, if it has one
   */
  _addToCart(trigger, id, preorder, note) {
    if (trigger.hasAttribute("aria-busy")) return;

    trigger.setAttribute("aria-busy", "true");

    window.addToCart(
      {
        id,
        quantity: 1,
        ...((preorder || note) && {
          properties: {
            ...(preorder && { Preorder: preorder }),
            ...(note && { Note: note }),
          },
        }),
      },
      {
        onAfterAdd: () => {
          trigger.removeAttribute("aria-busy");
          document.dispatchEvent(new CustomEvent("mini-cart:open"));
        },
        onError: (response) => {
          trigger.removeAttribute("aria-busy");
          this._showError(
            response?.description ||
              response?.message ||
              window.localeStrings.product.add_to_cart_error,
          );
        },
      },
    );
  }

  _showError(message) {
    const error = this.querySelector("[data-quick-add-error]");
    if (!error) return;

    error.textContent = message;

    clearTimeout(this._errorTimeout);
    this._errorTimeout = setTimeout(() => {
      error.textContent = "";
    }, ERROR_DURATION);
  }

  _changeContent(id) {
    this._loadData().then((data) => {
      const productData = data.find(({ variant_id }) => variant_id === id);

      if (!productData) {
        console.warn(`No product card data found for ${id}`);
        return;
      }

      const html = new DOMParser().parseFromString(
        productData.markup,
        "text/html",
      );

      const newProductCardMarkup = html.querySelector("product-card");

      morph(this, newProductCardMarkup);
      bindBloomreachEvents();
    });
  }

  _loadData() {
    if (this.data) return Promise.resolve(this.data);
    if (this.loadingData) return this.loadingData;
    const url = new URL(`${window.location.origin}${this.productUrl}`);

    url.searchParams.set("section_id", "product-card-content");
    this.loadingData = fetch(url)
      .then((res) => res.text())
      .then((markup) => {
        const html = new DOMParser().parseFromString(markup, "text/html");
        this.data = JSON.parse(html.querySelector("script").innerHTML);
        return this.data;
      });

    return this.loadingData;
  }

  // The gallery is the one part of the card not covered by a real link on
  // touch devices: an overlay link there would swallow the swipe. A tap
  // navigates from here instead, and a swipe is left to scroll the carousel.
  _navigateOnTap() {
    let origin = null;
    let dragged = false;

    const start = (x, y) => {
      origin = { x, y };
      dragged = false;
    };

    const move = (x, y) => {
      if (!origin || dragged) return;

      if (Math.hypot(x - origin.x, y - origin.y) > DRAG_THRESHOLD) {
        dragged = true;
      }
    };

    this.addEventListener("pointerdown", (event) => {
      start(event.clientX, event.clientY);
    });

    this.addEventListener("pointermove", (event) => {
      move(event.clientX, event.clientY);
    });

    // touchmove keeps firing once native scrolling claims the gesture, where
    // pointermove stops. Only pointer movement is trusted as a drag signal:
    // the carousel's own position shifts on gesture start when Swiper corrects
    // a looped slide, which would read as a drag on a stationary tap.
    this.addEventListener(
      "touchstart",
      (event) => {
        const touch = event.touches[0];
        if (touch) start(touch.clientX, touch.clientY);
      },
      { passive: true },
    );

    this.addEventListener(
      "touchmove",
      (event) => {
        const touch = event.touches[0];
        if (touch) move(touch.clientX, touch.clientY);
      },
      { passive: true },
    );

    this.addEventListener("click", (event) => {
      // Everywhere else is a real link already — the stretched title link over
      // the content, and the gallery's own overlay link on hover devices.
      if (!event.target.closest("product-card-carousel")) return;

      // The overlay link and the navigation arrows handle their own clicks.
      if (event.target.closest("a, button")) return;

      // Leave modifier-clicks to the browser rather than forcing a same-tab
      // navigation, for touchscreens that also have a keyboard.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      if (dragged) {
        dragged = false;
        return;
      }

      if (this.productUrl) window.location.assign(this.productUrl);
    });
  }

  get productUrl() {
    return this.getAttribute("product-url");
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}
